/**
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import React, { CSSProperties } from 'react';
import { Tag } from 'src/components';
import { useTheme } from '@superset-ui/core';
import {
  GHOST_CHIP_FONT_SIZE,
  GHOST_CHIP_MIN_HEIGHT,
  GHOST_CHIP_PADDING_X,
  GHOST_CHIP_PADDING_Y,
} from 'src/components/ghostChipStyles';

export type OnClickHandler = React.MouseEventHandler<HTMLElement>;

export type Type =
  | 'alert'
  | 'success'
  | 'warning'
  | 'danger'
  | 'info'
  | 'default'
  | 'primary'
  | 'secondary';

export type LabelVariant = 'default' | 'ghost';

export interface LabelProps extends React.HTMLAttributes<HTMLSpanElement> {
  key?: string;
  className?: string;
  onClick?: OnClickHandler;
  type?: Type;
  variant?: LabelVariant;
  style?: CSSProperties;
  children?: React.ReactNode;
  role?: string;
}

export default function Label(props: LabelProps) {
  const theme = useTheme();
  const { colors, transitionTiming } = theme;
  const {
    type = 'default',
    variant = 'default',
    onClick,
    children,
    ...rest
  } = props;
  const {
    alert,
    primary,
    secondary,
    grayscale,
    success,
    warning,
    error,
    info,
  } = colors;

  const isNeutralChip = type === 'default';
  const isGhost = variant === 'ghost';
  const useCompactChip = isNeutralChip && !isGhost;

  let backgroundColor = grayscale.light3;
  let backgroundColorHover = onClick ? primary.light2 : grayscale.light3;
  let borderColor = onClick ? grayscale.light2 : 'transparent';
  let borderColorHover = onClick ? primary.light1 : 'transparent';
  let color = grayscale.dark1;
  let colorHover = color;
  let useChipMetrics = false;

  if (isNeutralChip || isGhost) {
    useChipMetrics = isGhost;
    backgroundColor = grayscale.light5;
    borderColor = grayscale.light2;
    color = grayscale.dark1;
    backgroundColorHover = onClick ? primary.light2 : grayscale.light5;
    borderColorHover = onClick ? primary.light1 : grayscale.light2;
    colorHover = onClick ? primary.dark1 : grayscale.dark1;

    if (isGhost && type === 'danger') {
      backgroundColor = error.light2;
      borderColor = error.light1;
      color = error.dark1;
      backgroundColorHover = error.light2;
      borderColorHover = error.base;
      colorHover = error.dark2;
    } else if (isGhost && type === 'success') {
      backgroundColor = success.light2;
      borderColor = success.light1;
      color = success.dark1;
      backgroundColorHover = success.light2;
      borderColorHover = success.base;
      colorHover = success.dark2;
    } else if (isGhost && type === 'warning') {
      backgroundColor = warning.light2;
      borderColor = warning.light1;
      color = warning.dark1;
      backgroundColorHover = warning.light2;
      borderColorHover = warning.base;
      colorHover = warning.dark2;
    }
  } else {
    color = grayscale.light4;

    let baseColor;
    if (type === 'alert') {
      color = grayscale.dark1;
      baseColor = alert;
    } else if (type === 'success') {
      baseColor = success;
    } else if (type === 'warning') {
      baseColor = warning;
    } else if (type === 'danger') {
      baseColor = error;
    } else if (type === 'info') {
      baseColor = info;
    } else if (type === 'secondary') {
      baseColor = secondary;
    } else {
      baseColor = primary;
    }

    backgroundColor = baseColor.base;
    backgroundColorHover = onClick ? baseColor.dark1 : baseColor.base;
    borderColor = onClick ? baseColor.dark1 : 'transparent';
    borderColorHover = onClick ? baseColor.dark2 : 'transparent';
  }

  return (
    <Tag
      onClick={onClick}
      {...rest}
      css={{
        transition: `border-color ${transitionTiming}s, background-color ${transitionTiming}s, color ${transitionTiming}s`,
        whiteSpace: 'nowrap',
        cursor: onClick ? 'pointer' : 'default',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        backgroundColor,
        borderColor,
        borderRadius: isNeutralChip || isGhost ? 999 : 21,
        padding: useChipMetrics
          ? `${GHOST_CHIP_PADDING_Y}px ${GHOST_CHIP_PADDING_X}px`
          : isNeutralChip
          ? '2px 8px'
          : '0.35em 0.8em',
        minHeight: useChipMetrics ? GHOST_CHIP_MIN_HEIGHT : undefined,
        display: isNeutralChip || isGhost ? 'inline-flex' : undefined,
        alignItems: isNeutralChip || isGhost ? 'center' : undefined,
        gap: useChipMetrics ? 4 : undefined,
        boxSizing: useChipMetrics ? 'border-box' : undefined,
        fontSize: isNeutralChip || isGhost ? GHOST_CHIP_FONT_SIZE : undefined,
        lineHeight: isNeutralChip || isGhost ? 1.4 : 1,
        margin: useCompactChip ? undefined : useChipMetrics ? 0 : undefined,
        borderWidth: isNeutralChip || isGhost ? 1 : undefined,
        borderStyle: isNeutralChip || isGhost ? 'solid' : undefined,
        color,
        maxWidth: '100%',
        '&:hover': {
          backgroundColor: backgroundColorHover,
          borderColor: borderColorHover,
          color: isNeutralChip || isGhost ? colorHover : color,
          opacity: 1,
        },
      }}
    >
      {children}
    </Tag>
  );
}
